"use client";

// WO-GF-001-P1B-S13 (RW3-8 · founder ruling (b)) — THE MOUNTAIN MEMORY.
//
// Ruling (b) says content pages must KEEP the mountain HUD context. "Context" needs a source, and the only
// honest one is the mountain the visitor was actually on: /mountain-<code>… pages record it here and the
// content pages read it back. Nothing is inferred from the content page itself (it carries no mountain
// token), and nothing is fetched.
//
// COLD-START FALLBACK: a visitor who lands straight on /faq has no memory yet. Rather than show the orange
// pill the ruling kills, the HUD resolves to WHISTLER — the established precedent for exactly this
// "content page carries no mountain token" case (the S7 footer quicklinks and the RW-15(b) hamburger Info
// items both resolve to live's Whistler defaults). Recorded here so the default is visible, not accidental.

import { RESORTS } from "./resorts";

const KEY = "gf.mountain";
const BADGE_KEY = "gf.mountain.badge";
export const DEFAULT_MOUNTAIN = "whistler";

/**
 * Remember the mountain a hub/sub page is showing, together with the badge image THAT PAGE actually
 * renders. No-ops where storage is unavailable.
 *
 * S16 · F102 — the badge URL is now REMEMBERED, not derived. It used to be built by string template
 * (`mountain-<code>-logo.jpeg` on S3), which resolves for Whistler and 404s for every other mountain;
 * the resulting broken image is the "?" placeholder the founder saw jammed into the orange pill after
 * navigating from Marketplace/Big White. The mountain pages carry live's real badge markup, so we copy
 * the src we were actually served instead of guessing one.
 */
export function writeMountainContext(code: string, badge?: string | null): void {
  if (!code) return;
  try {
    window.localStorage.setItem(KEY, code);
    if (badge) window.localStorage.setItem(BADGE_KEY, `${code}|${badge}`);
  } catch {
    // private mode / storage disabled → the content HUD simply falls back to the default.
  }
}

/** The remembered badge URL for a mountain, or null when we have never been served one. */
export function readMountainBadge(code: string): string | null {
  try {
    const raw = window.localStorage.getItem(BADGE_KEY) || "";
    const sep = raw.indexOf("|");
    if (sep < 0) return null;
    return raw.slice(0, sep) === code ? raw.slice(sep + 1) || null : null;
  } catch {
    return null;
  }
}

/** The remembered mountain, or the recorded Whistler default. */
export function readMountainContext(): string {
  try {
    return window.localStorage.getItem(KEY) || DEFAULT_MOUNTAIN;
  } catch {
    return DEFAULT_MOUNTAIN;
  }
}

/** Display name from the captured resort sitemap (live's own 30 labels) — never a slug prettified. */
export function mountainLabel(code: string): string {
  const hit = RESORTS.find((r) => r.href.startsWith(`/mountain-${code}/`) || r.href === `/mountain-${code}`);
  return hit?.label.trim() || code;
}

/**
 * The badge asset to show for a mountain, or null when we have none.
 *
 * S16 · F102 — ONLY a URL we were actually served is returned. The previous string-template guess
 * (`…/mountain-<code>-logo.jpeg`) was verified on 2026-07-22 to resolve for `whistler` and 404 for
 * big-white, banff-sunshine, vail, stowe, sun-peaks, revelstoke, lake-louise, fernie and panorama —
 * i.e. for essentially every mountain except the one it was written against. Callers must handle null
 * by rendering the mountain's NAME, never a broken <img>.
 */
export function mountainBadgeSrc(code: string): string | null {
  const remembered = readMountainBadge(code);
  if (remembered) return remembered;
  // Whistler's asset is the one path proven to resolve, and it is also the cold-start default above.
  if (code === DEFAULT_MOUNTAIN) {
    return "https://snowbus-dev-strapi-image.s3.amazonaws.com/mountain/mountain-whistler-logo.jpeg";
  }
  return null;
}
